import React, { useState, useEffect } from 'react';
import { X, Download, FileText, File, FileSpreadsheet, Image } from 'lucide-react';
import { meetingAPI } from '../../services/api';

interface FilePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  meetingId: number;
  file: {
    id: number;
    original_filename: string;
    file_type: string;
    file_size: number;
  };
}

const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
  isOpen,
  onClose,
  meetingId,
  file
}) => {
  const [previewUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && file) {
      loadPreview();
    }
    
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [isOpen, file]);

  const loadPreview = async () => {
    if (!file) return;

    setIsLoading(true);
    setError(null);

    try {
      const blob = await meetingAPI.downloadFile(meetingId, file.id);
      const url = URL.createObjectURL(blob);
      
      // Open file in new browser window for better viewing
      const newWindow = window.open(url, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
      
      if (!newWindow) {
        setError('Please allow popups to view files in new window');
        return;
      }
      
      // Close the modal since we opened in new window
      onClose();
      
    } catch (error) {
      console.error('Error loading file preview:', error);
      setError('Failed to load file preview');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const blob = await meetingAPI.downloadFile(meetingId, file.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.original_filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const getFileIcon = (filename: string) => {
    const extension = filename.split('.').pop()?.toLowerCase();
    
    if (['pdf'].includes(extension || '')) {
      return <FileText className="h-8 w-8 text-red-500" />;
    } else if (['doc', 'docx'].includes(extension || '')) {
      return <FileText className="h-8 w-8 text-blue-500" />;
    } else if (['xls', 'xlsx'].includes(extension || '')) {
      return <FileSpreadsheet className="h-8 w-8 text-green-500" />;
    } else if (['ppt', 'pptx'].includes(extension || '')) {
      return <FileText className="h-8 w-8 text-orange-500" />;
    } else if (['jpg', 'jpeg', 'png', 'gif'].includes(extension || '')) {
      return <Image className="h-8 w-8 text-purple-500" />;
    } else {
      return <File className="h-8 w-8 text-gray-500" />;
    }
  };

  const canPreview = (filename: string) => {
    const extension = filename.split('.').pop()?.toLowerCase();
    return ['pdf', 'jpg', 'jpeg', 'png', 'gif'].includes(extension || '');
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!isOpen || !file) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            {getFileIcon(file.original_filename)}
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{file.original_filename}</h3>
              <p className="text-sm text-gray-500">{formatFileSize(file.file_size)}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleDownload}
              className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download className="h-4 w-4" />
              <span>Download</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Opening file in new window...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <File className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">{error}</p>
                <button
                  onClick={loadPreview}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : !canPreview(file.original_filename) ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                {getFileIcon(file.original_filename)}
                <p className="text-gray-600 mt-4 mb-4">Preview not available for this file type</p>
                <p className="text-sm text-gray-500 mb-4">
                  Supported preview formats: PDF, JPG, PNG, GIF
                </p>
                <button
                  onClick={handleDownload}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                >
                  Download File
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full p-8">
              <div className="text-center max-w-md">
                {getFileIcon(file.original_filename)}
                <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2">Open File Preview</h3>
                <p className="text-gray-600 mb-6">
                  Click "Open Preview" to view this file in a new browser window with a larger, more comfortable viewing area.
                </p>
                <div className="space-y-3">
                  <button
                    onClick={loadPreview}
                    className="w-full bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors font-medium"
                  >
                    Open Preview in New Window
                  </button>
                  <button
                    onClick={handleDownload}
                    className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    Download File
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-4">
                  Note: If the preview doesn't open, please check your browser's popup blocker settings.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilePreviewModal;
